/**
 * Cache partagé + dédup in-flight pour :
 * - GET /admin-menu-structure
 * - GET /granular-permissions/:userId
 *
 * Objectif : éviter 2–3 allers-retours identiques (App, Sidebar, AdminLayout, hooks).
 * Invalidation explicite après écriture (permissions / structure menu) + logout.
 */

import { config } from '@/config';

const API = config.BACKEND_URL || '/api';

/** TTL court : limite le risque de droits « périmés » sans refetch permanent. */
const DEFAULT_TTL_MS = 120_000;

let menuCache = null; // { at: number, data: any }
let menuInflight = null; // Promise<any> | null

/** @type {Map<string, { at: number, data: any }>} */
const permCache = new Map();
/** @type {Map<string, Promise<any>>} */
const permInflight = new Map();

export function invalidateAdminMenuStructure() {
  menuCache = null;
  menuInflight = null;
}

export function invalidateGranularPermissions(userId) {
  if (userId == null || userId === '') {
    permCache.clear();
    permInflight.clear();
    return;
  }
  const id = String(userId);
  permCache.delete(id);
  permInflight.delete(id);
}

export function invalidateAdminAccessCache() {
  invalidateAdminMenuStructure();
  invalidateGranularPermissions(null);
}

/**
 * @param {{ force?: boolean, ttlMs?: number }} [options]
 * @returns {Promise<any>}
 */
export async function fetchAdminMenuStructure(options = {}) {
  const { force = false, ttlMs = DEFAULT_TTL_MS } = options;
  const now = Date.now();

  if (force) {
    menuCache = null;
  } else if (menuCache && now - menuCache.at < ttlMs) {
    return menuCache.data;
  } else if (menuInflight) {
    return menuInflight;
  }

  menuInflight = (async () => {
    const res = await fetch(`${API}/admin-menu-structure`, { cache: 'no-store' });
    if (!res.ok) {
      throw new Error(`admin-menu-structure HTTP ${res.status}`);
    }
    const data = await res.json();
    menuCache = { at: Date.now(), data };
    return data;
  })();

  try {
    return await menuInflight;
  } finally {
    menuInflight = null;
  }
}

/**
 * @param {string} userId
 * @param {{ force?: boolean, ttlMs?: number, headers?: Record<string, string> }} [options]
 * @returns {Promise<any>}
 */
export async function fetchGranularPermissions(userId, options = {}) {
  if (!userId) {
    throw new Error('fetchGranularPermissions: userId requis');
  }
  const { force = false, ttlMs = DEFAULT_TTL_MS, headers = {} } = options;
  const id = String(userId);
  const now = Date.now();

  if (force) {
    permCache.delete(id);
    permInflight.delete(id);
  } else {
    const cached = permCache.get(id);
    if (cached && now - cached.at < ttlMs) {
      return cached.data;
    }
    const inflight = permInflight.get(id);
    if (inflight) {
      return inflight;
    }
  }

  const promise = (async () => {
    const res = await fetch(`${API}/granular-permissions/${encodeURIComponent(id)}`, {
      cache: 'no-store',
      headers,
    });
    if (!res.ok) {
      throw new Error(`granular-permissions HTTP ${res.status}`);
    }
    const data = await res.json();
    permCache.set(id, { at: Date.now(), data });
    return data;
  })();

  permInflight.set(id, promise);
  try {
    return await promise;
  } finally {
    permInflight.delete(id);
  }
}
